import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DemoTemplate } from '../../types';
import { listDemoTemplates, createDemoTemplate, updateDemoTemplate, deleteDemoTemplate, getProject } from '../../api';
import Modal from '../../components/Modal';

const DEFAULT_NAME_TEMPLATE = '示例 - {name}';

export default function DemoTemplates() {
  const { t } = useTranslation('settings');
  const [items, setItems] = useState<DemoTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null); // null=新增
  const [showForm, setShowForm] = useState(false);
  const [sourceProjectId, setSourceProjectId] = useState('');
  const [nameTemplate, setNameTemplate] = useState(DEFAULT_NAME_TEMPLATE);
  const [verifyResult, setVerifyResult] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listDemoTemplates());
      setError('');
    } catch (e: any) {
      setError(e.message || 'load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setSourceProjectId('');
    setNameTemplate(DEFAULT_NAME_TEMPLATE);
    setVerifyResult(null);
    setShowForm(true);
  };

  const openEdit = (tpl: DemoTemplate) => {
    setEditingId(tpl.id);
    setSourceProjectId(tpl.sourceProjectId);
    setNameTemplate(tpl.nameTemplate);
    setVerifyResult(null);
    setShowForm(true);
  };

  const handleVerify = async () => {
    if (!sourceProjectId.trim()) return;
    try {
      const p = await getProject(sourceProjectId.trim());
      setVerifyResult(`✓ ${p.name}`);
    } catch {
      setVerifyResult('✗ 项目不存在');
    }
  };

  const handleSave = async () => {
    if (!sourceProjectId.trim()) { setError('源项目ID必填'); return; }
    setSaving(true);
    setError('');
    try {
      if (editingId === null) {
        await createDemoTemplate(sourceProjectId.trim(), nameTemplate.trim() || DEFAULT_NAME_TEMPLATE);
      } else {
        await updateDemoTemplate(editingId, sourceProjectId.trim(), nameTemplate.trim() || DEFAULT_NAME_TEMPLATE);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e.message || 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl: DemoTemplate) => {
    if (!window.confirm(`删除示例项目模板 [${tpl.id} - ${tpl.nameTemplate}]？已克隆的用户不受影响。`)) return;
    try {
      await deleteDemoTemplate(tpl.id);
      await load();
    } catch (e: any) {
      setError(e.message || 'delete failed');
    }
  };

  return (
    <div style={{ padding: '1rem', maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>{t('demoTemplates')}</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ 新增模板</button>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: '0.5rem' }}>{error}</div>}
      {loading ? <div>加载中…</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>ID</th><th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>源项目ID</th><th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>名称模板</th><th style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>操作</th></tr></thead>
          <tbody>
            {items.map(tpl => (
              <tr key={tpl.id}>
                <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>{tpl.id}</td>
                <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>{tpl.sourceProjectId}</td>
                <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>{tpl.nameTemplate}</td>
                <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(tpl)}>编辑</button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(tpl)}>删除</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>暂无模板</td></tr>}
          </tbody>
        </table>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editingId === null ? '新增示例项目模板' : `编辑模板 #${editingId}`}
        width="480px"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>取消</button>
            <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? '保存中…' : '保存'}</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">源项目ID</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input className="input" style={{ flex: 1 }} value={sourceProjectId}
              onChange={e => { setSourceProjectId(e.target.value); setVerifyResult(null); }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={handleVerify}>校验</button>
          </div>
          {verifyResult && <div style={{ fontSize: '0.8rem', marginTop: '0.3rem' }}>{verifyResult}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">名称模板（{'{name}'} 会替换为源项目名）</label>
          <input className="input" value={nameTemplate} onChange={e => setNameTemplate(e.target.value)} />
          <div style={{ fontSize: '0.8rem', color: 'var(--text-3)', marginTop: '0.3rem' }}>
            预览：{nameTemplate.replace('{name}', '某项目')}
          </div>
        </div>
      </Modal>
    </div>
  );
}
